<template>
  <modal
    name="modal-presets"
    scrollable
    :width="'1080'"
    adaptive
    height="auto"
    @before-open="carregarTemplates"
    @before-close="resetarModal"
  >
    <div class="modal-presets">
      <div class="modal-header-content">
        <div class="title-container">
          <button
            v-if="exibirFormulario"
            type="button"
            class="back-button"
            @click="cancelarFormulario"
          >
            <i class="fas fa-arrow-left"></i>
          </button>
          <div v-else class="title-icon">
            <i class="fas fa-project-diagram"></i>
          </div>
          <div>
            <h2>
              {{
                exibirFormulario
                  ? templateEmEdicao
                    ? "Editar template"
                    : "Salvar novo template"
                  : "Templates de fluxo"
              }}
            </h2>
            <p v-if="!exibirFormulario">
              Comece por um modelo pronto ou salve o fluxo que está aberto.
            </p>
            <p v-else>
              {{
                templateEmEdicao
                  ? "Revise as informações e compare o desenho antes de salvar."
                  : "Confira o fluxo atual antes de transformá-lo em template."
              }}
            </p>
          </div>
        </div>
        <b-button
          v-if="!exibirFormulario"
          class="create-button"
          @click="iniciarCadastro"
        >
          <i class="fas fa-plus"></i>
          Salvar fluxo atual
        </b-button>
      </div>

      <div v-if="exibirFormulario" class="template-editor">
        <div class="template-form">
          <span class="eyebrow">Informações do template</span>
          <div class="form-grid">
            <b-form-group label="Nome do template" class="mb-0">
              <b-form-input
                v-model="form.nome"
                placeholder="Ex.: Boas-vindas"
              />
            </b-form-group>
            <b-form-group label="Descrição" class="mb-0">
              <b-form-input
                v-model="form.descricao"
                placeholder="Explique quando este fluxo deve ser utilizado"
              />
            </b-form-group>
          </div>
          <b-form-checkbox
            v-if="templateEmEdicao"
            v-model="form.usarFluxoAtual"
            class="replace-flow"
          >
            Substituir o desenho salvo pelo fluxo que está aberto
          </b-form-checkbox>
        </div>

        <div v-if="templateEmEdicao" class="comparison-section">
          <div class="comparison-heading">
            <div>
              <span class="eyebrow">Comparação do desenho</span>
              <h4>Antes e depois</h4>
            </div>
            <div class="changes-summary">
              <span
                v-for="alteracao in resumoAlteracoes"
                :key="alteracao.texto"
                :class="alteracao.tipo"
              >
                {{ alteracao.texto }}
              </span>
            </div>
          </div>
          <div class="comparison-grid">
            <div class="comparison-card">
              <div class="comparison-card-header">
                <div>
                  <span class="version-dot saved"></span>
                  <strong>Versão salva</strong>
                </div>
                <small>
                  {{ templateEmEdicao.nodes.length }} estágios ·
                  {{ templateEmEdicao.links.length }} conexões
                </small>
              </div>
              <FluxoTemplatePreview
                :fluxo="templateEmEdicao"
                :height="290"
                :node-statuses="statusVersaoSalva"
              />
            </div>
            <div
              class="comparison-card"
              :class="{ unchanged: !form.usarFluxoAtual }"
            >
              <div class="comparison-card-header">
                <div>
                  <span class="version-dot new"></span>
                  <strong>
                    {{ form.usarFluxoAtual ? "Nova versão" : "Versão mantida" }}
                  </strong>
                </div>
                <small>
                  {{ fluxoNovaVersao.nodes.length }} estágios ·
                  {{ fluxoNovaVersao.links.length }} conexões
                </small>
              </div>
              <FluxoTemplatePreview
                :fluxo="fluxoNovaVersao"
                :height="290"
                :node-statuses="statusNovaVersao"
              />
              <div v-if="!form.usarFluxoAtual" class="unchanged-message">
                <i class="fas fa-lock"></i>
                O desenho salvo será mantido
              </div>
            </div>
          </div>
        </div>

        <div v-else class="creation-preview">
          <div class="creation-preview-heading">
            <div>
              <span class="eyebrow">Desenho que será salvo</span>
              <h4>Preview do fluxo atual</h4>
            </div>
            <span class="flow-count">
              {{ fluxoAtual.nodes.length }} estágios ·
              {{ fluxoAtual.links.length }} conexões
            </span>
          </div>
          <FluxoTemplatePreview :fluxo="fluxoAtual" :height="360" />
        </div>

        <div class="editor-footer">
          <span class="flow-source">
            <i class="fas fa-info-circle"></i>
            Os campos sensíveis não serão armazenados no template.
          </span>
          <div class="form-actions">
            <b-button variant="light" @click="cancelarFormulario">
              Cancelar
            </b-button>
            <b-button
              class="save-button"
              :disabled="salvando"
              @click="salvarTemplate"
            >
              <i class="fas fa-save"></i>
              {{ salvando ? "Salvando..." : "Salvar template" }}
            </b-button>
          </div>
        </div>
      </div>

      <template v-else>
        <div v-if="carregando" class="loading-placeholder"></div>

        <div v-else-if="!todosOsTemplates.length" class="empty-state">
          <div class="empty-icon"><i class="fas fa-layer-group"></i></div>
          <h4>Nenhum template disponível</h4>
          <p>Salve o fluxo aberto para criar o primeiro template.</p>
          <b-button class="create-button" @click="iniciarCadastro">
            Criar primeiro template
          </b-button>
        </div>

        <div v-else class="templates-workspace">
          <aside class="templates-sidebar">
            <div class="sidebar-heading">
              <div>
                <span class="eyebrow">Biblioteca</span>
                <strong>{{ templatesFiltrados.length }} templates</strong>
              </div>
            </div>
            <div class="search-box">
              <i class="fas fa-search"></i>
              <b-form-input v-model="busca" placeholder="Buscar template" />
            </div>
            <div class="templates-list">
              <button
                v-for="preset in templatesFiltrados"
                :key="preset.chave"
                type="button"
                class="template-item"
                :class="{ active: itemSelecionado === preset.chave }"
                @click="itemSelecionado = preset.chave"
              >
                <span class="template-symbol">
                  <i
                    :class="
                      preset.padrao ? 'fas fa-star' : 'fas fa-project-diagram'
                    "
                  ></i>
                </span>
                <span class="template-copy">
                  <span class="template-name">{{
                    preset.nome || "Sem nome"
                  }}</span>
                  <span class="template-meta">
                    {{ preset.nodes.length }} estágios ·
                    {{ preset.links.length }} conexões
                  </span>
                </span>
                <i class="fas fa-chevron-right template-chevron"></i>
              </button>
              <div v-if="!templatesFiltrados.length" class="no-results">
                Nenhum template encontrado.
              </div>
            </div>
          </aside>

          <section class="template-preview">
            <div class="preview-heading">
              <div class="preview-title">
                <span
                  class="template-badge"
                  :class="{ custom: !presetAtivo.padrao }"
                >
                  <i
                    :class="presetAtivo.padrao ? 'fas fa-star' : 'fas fa-user'"
                  ></i>
                  {{ presetAtivo.padrao ? "Template padrão" : "Personalizado" }}
                </span>
                <h3>{{ presetAtivo.nome || "Sem nome" }}</h3>
                <p>{{ presetAtivo.descricao || "Template sem descrição." }}</p>
              </div>
              <div v-if="!presetAtivo.padrao" class="template-actions">
                <button
                  type="button"
                  title="Editar"
                  @click="iniciarEdicao(presetAtivo)"
                >
                  <i class="fas fa-pen"></i>
                </button>
                <button
                  type="button"
                  class="delete"
                  title="Remover"
                  @click="removerTemplate(presetAtivo)"
                >
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </div>

            <div class="flow-preview-canvas">
              <FluxoTemplatePreview :fluxo="presetAtivo" :height="390" />
              <div class="canvas-label">
                <i class="fas fa-eye"></i>
                Pré-visualização
              </div>
            </div>

            <div class="preview-footer">
              <div class="flow-stats">
                <span
                  ><strong>{{ presetAtivo.nodes.length }}</strong>
                  estágios</span
                >
                <span
                  ><strong>{{ presetAtivo.links.length }}</strong>
                  conexões</span
                >
              </div>
              <div class="footer-actions">
                <b-button variant="light" @click="$modal.hide('modal-presets')">
                  Fechar
                </b-button>
                <b-button class="use-template-button" @click="confirmarPreset">
                  Usar este template
                  <i class="fas fa-arrow-right"></i>
                </b-button>
              </div>
            </div>
          </section>
        </div>
      </template>
    </div>
  </modal>
</template>

<script>
import FluxoTemplatePreview from "./FluxoTemplatePreview.vue";
import API_LINKS from "../../api.links";
import ApiService from "@/core/services/api.service";
import Swal from "sweetalert2";

export default {
  components: {
    FluxoTemplatePreview
  },
  props: {
    fluxoAtual: {
      type: Object,
      default: () => ({ nodes: [], links: [] })
    },
    tituloFluxo: {
      type: String,
      default: ""
    },
    descricaoFluxo: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      itemSelecionado: null,
      busca: "",
      templates: [],
      carregando: false,
      salvando: false,
      exibirFormulario: false,
      templateEmEdicao: null,
      form: {
        nome: "",
        descricao: "",
        usarFluxoAtual: true
      }
    };
  },
  methods: {
    async carregarTemplates() {
      this.carregando = true;
      try {
        const response = await ApiService.get(API_LINKS.chatbot_fluxo_template);
        this.templates = response.data.templates || [];
        const templateSelecionadoExiste = this.todosOsTemplates.some(
          (template) => template.chave === this.itemSelecionado
        );
        if (!templateSelecionadoExiste) {
          this.itemSelecionado = this.todosOsTemplates.length
            ? this.todosOsTemplates[0].chave
            : null;
        }
      } finally {
        this.carregando = false;
      }
    },
    iniciarCadastro() {
      this.templateEmEdicao = null;
      this.form = {
        nome: this.tituloFluxo,
        descricao: this.descricaoFluxo,
        usarFluxoAtual: true
      };
      this.exibirFormulario = true;
    },
    iniciarEdicao(template) {
      this.templateEmEdicao = template;
      this.form = {
        nome: template.nome,
        descricao: template.descricao,
        usarFluxoAtual: false
      };
      this.exibirFormulario = true;
    },
    cancelarFormulario() {
      this.exibirFormulario = false;
      this.templateEmEdicao = null;
    },
    resetarModal() {
      this.cancelarFormulario();
      this.busca = "";
    },
    async salvarTemplate() {
      const fluxo =
        !this.templateEmEdicao || this.form.usarFluxoAtual
          ? this.fluxoAtual
          : this.templateEmEdicao;
      const payload = {
        template: {
          nome: this.form.nome,
          descricao: this.form.descricao,
          nodes: this.higienizarFluxo(
            JSON.parse(JSON.stringify(fluxo.nodes || []))
          ),
          links: JSON.parse(JSON.stringify(fluxo.links || []))
        }
      };

      this.salvando = true;
      try {
        let response;
        if (this.templateEmEdicao) {
          response = await ApiService.put(
            `${API_LINKS.chatbot_fluxo_template}/${this.templateEmEdicao.id}`,
            payload
          );
        } else {
          response = await ApiService.post(
            API_LINKS.chatbot_fluxo_template,
            payload
          );
        }
        await this.carregarTemplates();
        const templateSalvo = response.data.template;
        if (templateSalvo) {
          this.itemSelecionado = `template-${templateSalvo.chatbot_fluxo_template_id}`;
        }
        this.cancelarFormulario();
        Swal.fire("Pronto!", "Template salvo com sucesso.", "success");
      } finally {
        this.salvando = false;
      }
    },
    removerTemplate(template) {
      Swal.fire({
        title: `Remover o template '${template.nome}'?`,
        text: "Esta ação não poderá ser desfeita.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "Sim, remover",
        cancelButtonText: "Cancelar"
      }).then(async (result) => {
        if (!result.value) return;

        await ApiService.delete(
          `${API_LINKS.chatbot_fluxo_template}/${template.id}`,
          null
        );
        if (this.itemSelecionado === template.chave) {
          this.itemSelecionado = null;
        }
        await this.carregarTemplates();
      });
    },
    higienizarFluxo(valor) {
      const camposSensiveis = [
        "time",
        "time_id",
        "cfe_time",
        "modelo_id",
        "modelo_link_id",
        "chatbot_modelo_id",
        "sistema",
        "sistema_id",
        "sistema_licenciada_id",
        "agente",
        "agente_id"
      ];
      const camposEstruturais = [
        "chatbot_fluxo_id",
        "chatbot_fluxo_estagio_id"
      ];

      if (Array.isArray(valor)) {
        return valor.map((item) => this.higienizarFluxo(item));
      }
      if (!valor || typeof valor !== "object") return valor;

      const resultado = {};
      Object.keys(valor).forEach((chave) => {
        if (camposEstruturais.includes(chave)) return;
        resultado[chave] = camposSensiveis.includes(chave)
          ? null
          : this.higienizarFluxo(valor[chave]);
      });

      if (camposSensiveis.includes(resultado.bindText)) {
        resultado.value = null;
      }

      return resultado;
    },
    normalizarLista(valor) {
      if (Array.isArray(valor)) return valor;
      if (!valor) return [];

      if (typeof valor === "string") {
        try {
          return this.normalizarLista(JSON.parse(valor));
        } catch (error) {
          return [];
        }
      }

      return typeof valor === "object" ? Object.values(valor) : [];
    },
    confirmarPreset() {
      Swal.fire({
        title: `Usar o template '${this.presetAtivo.nome}'?`,
        text: "O desenho atual será substituído pelo conteúdo deste template.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#f5a000",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "Sim, usar template",
        cancelButtonText: "Cancelar"
      }).then((result) => {
        if (result.value) {
          const nodes = this.higienizarFluxo(
            JSON.parse(JSON.stringify(this.presetAtivo.nodes))
          );
          this.$emit(
            "confirmarPreset",
            nodes,
            JSON.parse(JSON.stringify(this.presetAtivo.links))
          );
          this.$modal.hide("modal-presets");
        }
      });
    }
  },
  computed: {
    todosOsTemplates() {
      return this.templates
        .map((template) => ({
          id: template.chatbot_fluxo_template_id,
          chave: `template-${template.chatbot_fluxo_template_id}`,
          nome: template.cft_nome,
          descricao: template.cft_descricao,
          nodes: this.normalizarLista(template.cft_nodes),
          links: this.normalizarLista(template.cft_links),
          padrao: Boolean(template.cft_padrao)
        }))
        .sort((a, b) => {
          if (a.padrao !== b.padrao) return a.padrao ? -1 : 1;
          return (a.nome || "").localeCompare(b.nome || "", "pt-BR");
        });
    },
    templatesFiltrados() {
      const busca = this.busca.trim().toLocaleLowerCase();
      if (!busca) return this.todosOsTemplates;

      return this.todosOsTemplates.filter((template) => {
        return `${template.nome || ""} ${template.descricao || ""}`
          .toLocaleLowerCase()
          .includes(busca);
      });
    },
    presetAtivo() {
      const ativo = this.todosOsTemplates.find(
        (template) => template.chave === this.itemSelecionado
      );
      return ativo ? ativo : {};
    },
    fluxoNovaVersao() {
      return this.form.usarFluxoAtual
        ? this.fluxoAtual
        : this.templateEmEdicao || { nodes: [], links: [] };
    },
    diferencasFluxo() {
      if (!this.form.usarFluxoAtual) {
        return {
          adicionados: 0,
          removidos: 0,
          alterados: 0,
          linksAdicionados: 0,
          linksRemovidos: 0,
          statusVersaoSalva: {},
          statusNovaVersao: {}
        };
      }

      const antigos = this.normalizarLista((this.templateEmEdicao || {}).nodes);
      const novos = this.normalizarLista((this.fluxoAtual || {}).nodes);
      const idsAntigos = new Set(antigos.map((node) => String(node.id)));
      const idsNovos = new Set(novos.map((node) => String(node.id)));
      const nodesAdicionados = novos.filter(
        (node) => !idsAntigos.has(String(node.id))
      );
      const nodesRemovidos = antigos.filter(
        (node) => !idsNovos.has(String(node.id))
      );
      const novosPorId = novos.reduce((map, node) => {
        map[String(node.id)] = node;
        return map;
      }, {});
      const nodesAlterados = antigos.filter((node) => {
        const novo = novosPorId[String(node.id)];
        if (!novo) return false;

        return (
          JSON.stringify(this.higienizarFluxo(node)) !==
          JSON.stringify(this.higienizarFluxo(novo))
        );
      });
      const linksAntigos = new Set(
        this.normalizarLista((this.templateEmEdicao || {}).links).map(
          (link) => `${link.from}->${link.to}`
        )
      );
      const linksNovos = new Set(
        this.normalizarLista((this.fluxoAtual || {}).links).map(
          (link) => `${link.from}->${link.to}`
        )
      );
      const linksAdicionados = [...linksNovos].filter(
        (link) => !linksAntigos.has(link)
      ).length;
      const linksRemovidos = [...linksAntigos].filter(
        (link) => !linksNovos.has(link)
      ).length;
      const statusVersaoSalva = {};
      const statusNovaVersao = {};

      nodesRemovidos.forEach((node) => {
        statusVersaoSalva[String(node.id)] = "removed";
      });
      nodesAdicionados.forEach((node) => {
        statusNovaVersao[String(node.id)] = "added";
      });
      nodesAlterados.forEach((node) => {
        statusVersaoSalva[String(node.id)] = "changed";
        statusNovaVersao[String(node.id)] = "changed";
      });

      return {
        adicionados: nodesAdicionados.length,
        removidos: nodesRemovidos.length,
        alterados: nodesAlterados.length,
        linksAdicionados,
        linksRemovidos,
        statusVersaoSalva,
        statusNovaVersao
      };
    },
    statusVersaoSalva() {
      return this.diferencasFluxo.statusVersaoSalva;
    },
    statusNovaVersao() {
      return this.diferencasFluxo.statusNovaVersao;
    },
    resumoAlteracoes() {
      const diferencas = this.diferencasFluxo;
      const alteracoes = [];

      if (diferencas.adicionados) {
        alteracoes.push({
          texto: `+${diferencas.adicionados} estágios`,
          tipo: "added"
        });
      }
      if (diferencas.removidos) {
        alteracoes.push({
          texto: `-${diferencas.removidos} estágios`,
          tipo: "removed"
        });
      }
      if (diferencas.alterados) {
        alteracoes.push({
          texto: `${diferencas.alterados} estágios alterados`,
          tipo: "changed"
        });
      }
      if (diferencas.linksAdicionados) {
        alteracoes.push({
          texto: `+${diferencas.linksAdicionados} conexões`,
          tipo: "added"
        });
      }
      if (diferencas.linksRemovidos) {
        alteracoes.push({
          texto: `-${diferencas.linksRemovidos} conexões`,
          tipo: "removed"
        });
      }

      if (!alteracoes.length) {
        alteracoes.push({ texto: "Mesma estrutura", tipo: "neutral" });
      }

      return alteracoes;
    }
  }
};
</script>

<style lang="scss" scoped>
.modal-presets {
  background: #f6f8fb;
  color: #263247;
  min-height: 650px;

  .modal-header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    padding: 24px 28px;
    border-bottom: 1px solid #e7ebf1;
    background: #fff;
  }

  .title-container {
    display: flex;
    align-items: center;
    gap: 14px;

    h2 {
      margin: 0 0 3px;
      color: #202a3b;
      font-size: 22px;
      font-weight: 700;
    }

    p {
      margin: 0;
      color: #7a8495;
      font-size: 13px;
    }
  }

  .title-icon,
  .back-button {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 44px;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    border: 0;
    background: #fff4df;
    color: #ed9400;
    font-size: 18px;
  }

  .back-button {
    cursor: pointer;

    &:hover {
      background: #ffeac3;
    }
  }

  .create-button,
  .save-button,
  .use-template-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
    border: 0 !important;
    border-radius: 8px;
    background: #f5a000 !important;
    color: #fff !important;
    font-weight: 600;
    box-shadow: 0 5px 14px rgba(245, 160, 0, 0.2);

    &:hover {
      background: #df9000 !important;
    }
  }

  .template-editor {
    padding: 22px 28px 28px;
  }

  .template-form {
    padding: 20px;
    border: 1px solid #dfe5ed;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 8px 26px rgba(39, 50, 71, 0.07);
  }

  .preview-heading,
  .preview-footer,
  .editor-footer,
  .comparison-heading,
  .creation-preview-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
  }

  .eyebrow {
    display: block;
    color: #9aa3b1;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .form-grid {
    display: grid;
    grid-template-columns: 0.8fr 1.4fr;
    gap: 16px;
    margin-top: 12px;
  }

  .replace-flow {
    margin-top: 16px;
    color: #586579;
    font-size: 12px;
  }

  .flow-source {
    color: #697488;
    font-size: 12px;

    i {
      margin-right: 5px;
      color: #35a769;
    }
  }

  .comparison-section,
  .creation-preview {
    margin-top: 18px;
    padding: 18px;
    overflow: hidden;
    border: 1px solid #dfe5ed;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 8px 26px rgba(39, 50, 71, 0.07);
  }

  .comparison-heading,
  .creation-preview-heading {
    margin-bottom: 14px;

    h4 {
      margin: 2px 0 0;
      color: #2c3749;
      font-size: 16px;
      font-weight: 700;
    }
  }

  .changes-summary {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 6px;

    span {
      padding: 4px 8px;
      border-radius: 20px;
      font-size: 10px;
      font-weight: 700;

      &.added {
        background: #e5f7ed;
        color: #278452;
      }

      &.removed {
        background: #fdeaea;
        color: #c54b4b;
      }

      &.changed {
        background: #e9f1ff;
        color: #3975ca;
      }

      &.neutral {
        background: #eef1f5;
        color: #707b8e;
      }
    }
  }

  .comparison-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
  }

  .comparison-card {
    position: relative;
    min-width: 0;
    overflow: hidden;
    border: 1px solid #dfe5ed;
    border-radius: 10px;
    background: #fafbfd;

    &:last-child {
      border-color: #a9c8f3;
      box-shadow: 0 0 0 1px rgba(57, 117, 202, 0.08);
    }

    &.unchanged {
      border-color: #dfe5ed;
      box-shadow: none;
    }
  }

  .comparison-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 11px 13px;
    border-bottom: 1px solid #e6eaf0;
    background: #fff;
    font-size: 11px;

    > div {
      display: flex;
      align-items: center;
      gap: 7px;
    }

    small {
      color: #8c96a5;
      font-size: 9px;
    }
  }

  .version-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;

    &.saved {
      background: #98a2b1;
    }

    &.new {
      background: #3975ca;
    }
  }

  .unchanged-message {
    position: absolute;
    right: 10px;
    bottom: 10px;
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 5px 8px;
    border: 1px solid #dfe4eb;
    border-radius: 7px;
    background: rgba(255, 255, 255, 0.94);
    color: #778295;
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
  }

  .creation-preview {
    padding-bottom: 0;

    ::v-deep .flow-template-preview {
      border-top: 1px solid #e5e9ef;
    }
  }

  .flow-count {
    color: #7d8797;
    font-size: 11px;
  }

  .editor-footer {
    margin-top: 18px;
    padding: 16px 18px;
    border: 1px solid #dfe5ed;
    border-radius: 12px;
    background: #fff;
  }

  .form-actions,
  .footer-actions,
  .flow-stats {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .loading-placeholder {
    min-height: 550px;
  }

  .empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    min-height: 500px;
    color: #7c8798;
    gap: 12px;
  }

  .empty-state {
    h4 {
      margin: 4px 0 0;
      color: #303b4e;
    }

    p {
      margin: 0 0 8px;
    }
  }

  .empty-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 68px;
    height: 68px;
    border-radius: 20px;
    background: #edf1f6;
    color: #96a0af;
    font-size: 25px;
  }

  .templates-workspace {
    display: grid;
    grid-template-columns: 310px minmax(0, 1fr);
    min-height: 550px;
    margin: 20px 28px 28px;
    overflow: hidden;
    border: 1px solid #dfe5ed;
    border-radius: 14px;
    background: #fff;
    box-shadow: 0 10px 32px rgba(39, 50, 71, 0.08);
  }

  .templates-sidebar {
    min-width: 0;
    padding: 20px 16px;
    border-right: 1px solid #e7ebf1;
    background: #fbfcfe;
  }

  .sidebar-heading {
    display: flex;
    justify-content: space-between;
    margin: 0 4px 14px;

    strong {
      display: block;
      margin-top: 2px;
      font-size: 14px;
    }
  }

  .search-box {
    position: relative;
    margin-bottom: 14px;

    i {
      position: absolute;
      z-index: 1;
      top: 12px;
      left: 12px;
      color: #a5adba;
      font-size: 12px;
    }

    input {
      height: 38px;
      padding-left: 34px;
      border-color: #e0e5ec;
      border-radius: 8px;
      background: #fff;
      font-size: 12px;
    }
  }

  .templates-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 450px;
    padding-right: 3px;
    overflow-y: auto;
  }

  .template-item {
    display: flex;
    align-items: center;
    width: 100%;
    gap: 11px;
    padding: 11px;
    border: 1px solid transparent;
    border-radius: 10px;
    background: transparent;
    color: inherit;
    text-align: left;
    cursor: pointer;
    transition: 0.18s ease;

    &:hover {
      border-color: #e2e7ee;
      background: #fff;
    }

    &.active {
      border-color: #ffd58a;
      background: #fff8eb;

      .template-symbol {
        background: #f5a000;
        color: #fff;
      }

      .template-chevron {
        color: #e99700;
      }
    }
  }

  .template-symbol {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 36px;
    width: 36px;
    height: 36px;
    border-radius: 9px;
    background: #edf1f6;
    color: #768195;
    font-size: 13px;
  }

  .template-copy {
    display: flex;
    flex: 1;
    min-width: 0;
    flex-direction: column;
  }

  .template-name {
    overflow: hidden;
    font-size: 13px;
    font-weight: 700;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .template-meta {
    margin-top: 2px;
    color: #929bab;
    font-size: 10px;
  }

  .template-chevron {
    color: #c1c7d0;
    font-size: 10px;
  }

  .no-results {
    padding: 28px 10px;
    color: #949dac;
    font-size: 12px;
    text-align: center;
  }

  .template-preview {
    display: flex;
    min-width: 0;
    padding: 24px;
    flex-direction: column;
  }

  .preview-heading {
    align-items: flex-start;
    min-height: 91px;
  }

  .preview-title {
    min-width: 0;

    h3 {
      margin: 8px 0 4px;
      color: #222d40;
      font-size: 21px;
      font-weight: 700;
    }

    p {
      max-width: 570px;
      margin: 0;
      overflow: hidden;
      color: #7c8798;
      font-size: 12px;
      line-height: 1.5;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .template-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 8px;
    border-radius: 20px;
    background: #fff1d6;
    color: #b97600;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;

    &.custom {
      background: #e9f1ff;
      color: #3975ca;
    }
  }

  .template-actions {
    display: flex;
    flex-shrink: 0;
    gap: 8px;

    button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 34px;
      height: 34px;
      border: 1px solid #e0e5ec;
      border-radius: 8px;
      background: #fff;
      color: #7c8798;
      cursor: pointer;

      &:hover {
        border-color: #b9c8dc;
        color: #3975ca;
      }

      &.delete:hover {
        border-color: #f1b7b7;
        color: #d84d4d;
      }
    }
  }

  .flow-preview-canvas {
    position: relative;
    height: 390px;
    overflow: hidden;
    border: 1px solid #e1e6ed;
    border-radius: 12px;
    background: #fafbfd;
  }

  .canvas-label {
    position: absolute;
    top: 12px;
    left: 12px;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 5px 8px;
    border: 1px solid #e3e7ed;
    border-radius: 7px;
    background: rgba(255, 255, 255, 0.92);
    color: #8a94a4;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .preview-footer {
    margin-top: auto;
    padding-top: 18px;
  }

  .flow-stats {
    color: #8a94a4;
    font-size: 11px;

    span + span {
      padding-left: 10px;
      border-left: 1px solid #dfe4eb;
    }

    strong {
      color: #3a4659;
      font-size: 13px;
    }
  }

  .use-template-button {
    padding-right: 16px;
    padding-left: 16px;
  }

  @media (max-width: 850px) {
    min-height: auto;

    .modal-header-content,
    .preview-footer,
    .editor-footer,
    .comparison-heading,
    .creation-preview-heading {
      align-items: stretch;
      flex-direction: column;
    }

    .templates-workspace {
      grid-template-columns: 1fr;
    }

    .templates-sidebar {
      border-right: 0;
      border-bottom: 1px solid #e7ebf1;
    }

    .templates-list {
      max-height: 220px;
    }

    .form-grid {
      grid-template-columns: 1fr;
    }

    .comparison-grid {
      grid-template-columns: 1fr;
    }

    .changes-summary {
      justify-content: flex-start;
    }

    .form-actions,
    .footer-actions {
      justify-content: flex-end;
    }

    .flow-preview-canvas {
      height: 305px;
    }
  }

  @media (max-width: 540px) {
    .modal-header-content,
    .template-preview {
      padding: 18px;
    }

    .templates-workspace {
      margin-right: 14px;
      margin-left: 14px;
    }

    .template-editor {
      padding: 14px;
    }

    .title-container p {
      display: none;
    }

    .footer-actions {
      flex-direction: column-reverse;

      .btn {
        width: 100%;
      }
    }
  }
}
</style>
